/* SellerCatalog — uses the shared CatalogLayoutShell + CatalogProductCard so it
 * is in exact layout/feature/visible-field parity with BuyerCatalog. Only the
 * row-level CTA differs (seller → Edit/Delete · buyer → Add to order). The
 * seller-only chrome (catalog-app toggle, AI import, setup wizard, CRUD + CSV
 * modals) is kept intact. */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { money, formatErr } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  Plus, Trash2, Upload, Layers, ShieldAlert, Sparkles,
  Check, Settings2, Plug, Mail, ExternalLink, Sliders, Share2,
  ChevronDown, MoreHorizontal, QrCode, X,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import CatalogNeedsAttention from "../../components/catalog-import/CatalogNeedsAttention";
import CustomizeCatalogPanel from "../../components/catalog-shared/CustomizeCatalogPanel";
import ShareCatalogModal from "../../components/seller/ShareCatalogModal";
import {
  CatalogLayoutShell, CatalogProductCard, CatalogTextTable, SELLER_TESTIDS, useAttrFacets,
} from "../../components/catalog-shared";

const EMPTY = {
  name: "", sku: "", category: "Beauty", brand: "", price: 0, moq: 1, stock: 0,
  image: "", description: "", map_price: "", retail_price: "", dropship_handling_fee: "", tiers: [],
  sub_category: "", variant: "", size: "", product_type: "", gender: "",
  ean: "", asin: "", condition: "", exp_date: "", upc: "", custom_fields: [],
};
// custom_fields may arrive as an array (new rows) or an object map (older
// imported rows). Always normalize to [{label, value}] for the edit form.
function normalizeCustomFields(cf) {
  if (Array.isArray(cf)) {
    return cf.map(c => ({ label: c?.label || "", value: c?.value || "" }));
  }
  if (cf && typeof cf === "object") {
    return Object.entries(cf).map(([label, value]) => ({
      label: String(label || ""),
      value: value == null ? "" : String(value),
    }));
  }
  return [];
}
const SEARCH_FIELDS = [
  { value: "all", label: "All fields" },
  { value: "name", label: "Name" },
  { value: "sku", label: "SKU" },
  { value: "upc", label: "UPC" },
  { value: "product_type", label: "Type" },
  { value: "size", label: "Size" },
];
const SORT_OPTS = [
  { value: "name_asc", label: "Name: A → Z" },
  { value: "name_desc", label: "Name: Z → A" },
  { value: "price_asc", label: "Price: low → high" },
  { value: "price_desc", label: "Price: high → low" },
  { value: "stock_desc", label: "Stock: high → low" },
];

export default function SellerCatalog() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const [catalogFeature, setCatalogFeature] = useState({ feature_enabled: false, enabled: false });
  const [apiEnabled, setApiEnabled] = useState(null);
  const [storefrontUrl, setStorefrontUrl] = useState(null);
  const [visibleFields, setVisibleFields] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [setupState, setSetupState] = useState(null);
  const [setupDoneDismissed, setSetupDoneDismissed] = useState(false);
  useEffect(() => {
    api.get("/seller/catalog-import/app-status")
      .then(r => setCatalogFeature(r.data || { feature_enabled: false, enabled: false }))
      .catch(() => setCatalogFeature({ feature_enabled: false, enabled: false }));
    api.get("/seller/catalog-api/settings")
      .then(r => setApiEnabled(!!r.data?.enabled))
      .catch(() => setApiEnabled(false));
    api.get("/seller/catalog-import/visibility")
      .then(r => setVisibleFields(r.data?.fields || {}))
      .catch(() => setVisibleFields({}));
    api.get("/tenant/me")
      .then(r => { if (r.data?.slug) setStorefrontUrl(`${window.location.origin}/s/${r.data.slug}`); })
      .catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    if (!catalogFeature.feature_enabled) return;
    api.get("/seller/catalog-setup/state")
      .then(r => {
        const s = r.data || {};
        setSetupState(s);
        if (!s.completed_at && !s.skipped) nav("/seller/catalog/setup", { replace: true });
      })
      .catch(() => { /* ignore */ });
  }, [catalogFeature.feature_enabled, nav]);

  const [prePublish, setPrePublish] = useState(null);
  const toggleCatalogApp = async () => {
    if (catalogFeature.enabled) {
      try {
        const r = await api.put("/seller/catalog-import/app-enabled", { enabled: false });
        setCatalogFeature(s => ({ ...s, enabled: r.data.enabled }));
        toast.success("Catalog app paused — buyers can no longer see it");
      } catch (e) { toast.error(e.response?.data?.detail?.message || "Could not toggle"); }
      return;
    }
    try {
      const r = await api.get("/seller/catalog-import/completeness");
      const critical = (r.data?.gaps || []).filter(g => !["stock", "moq"].includes(g.field));
      if (critical.length > 0) { setPrePublish({ gaps: critical, total: r.data?.total_products || 0 }); return; }
    } catch (_e) { /* if completeness fails, just flip */ }
    await _flipCatalogOn();
  };
  const _flipCatalogOn = async () => {
    try {
      const r = await api.put("/seller/catalog-import/app-enabled", { enabled: true });
      setCatalogFeature(s => ({ ...s, enabled: r.data.enabled }));
      toast.success("Catalog app enabled — buyers can now browse it");
    } catch (e) { toast.error(e.response?.data?.detail?.message || "Could not toggle"); }
  };

  // Filters + view state
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [badgeSet, setBadgeSet] = useState(new Set());
  const [cats, setCats] = useState(new Set());
  const [brandSet, setBrandSet] = useState(new Set());
  const [fulfillment, setFulfillment] = useState("all");
  const [maxPrice, setMaxPrice] = useState(500);
  const [minMoq, setMinMoq] = useState(1);
  const [sort, setSort] = useState("name_asc");
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("verqo:seller-catalog:view") || "grid"; } catch (_e) { return "grid"; }
  });
  useEffect(() => { try { localStorage.setItem("verqo:seller-catalog:view", viewMode); } catch (_e) { /* ignore */ } }, [viewMode]);

  // CRUD state
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const csvRef = useRef(null);

  const load = () => api.get("/products").then(r => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const m = new Map();
    items.forEach(p => { if (p.category) m.set(p.category, (m.get(p.category) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([key, count]) => ({ key, count }));
  }, [items]);

  const brands = useMemo(() => {
    const m = new Map();
    items.forEach(p => { if (p.brand) m.set(p.brand, (m.get(p.brand) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([key, count]) => ({ key, count }));
  }, [items]);

  const { attrFilters, applyAttrFacets, attrSelKey } = useAttrFacets(items);

  const filtered = useMemo(() => {
    let r = items;
    if (badgeSet.size > 0) r = r.filter(p => badgeSet.has(p.badge));
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(p => {
        if (searchField === "name") return p.name?.toLowerCase().includes(q);
        if (searchField === "sku")  return p.sku?.toLowerCase().includes(q);
        if (searchField === "upc")  return p.upc?.toLowerCase().includes(q);
        if (searchField === "product_type") return p.product_type?.toLowerCase().includes(q);
        if (searchField === "size") return String(p.size || "").toLowerCase().includes(q);
        return [p.name, p.sku, p.upc, p.category, p.brand, p.sub_category, p.product_type, p.size, p.gender, p.condition, p.variant, p.ean, p.asin].some(v => v != null && String(v).toLowerCase().includes(q));
      });
    }
    if (cats.size > 0) r = r.filter(p => cats.has(p.category));
    if (brandSet.size > 0) r = r.filter(p => brandSet.has(p.brand));
    r = applyAttrFacets(r);
    if (fulfillment !== "all") r = r.filter(p => p.fulfillment === fulfillment || (fulfillment === "wholesale" && !p.fulfillment));
    if (maxPrice < 500) r = r.filter(p => (p.price || 0) <= maxPrice);
    if (minMoq > 1) r = r.filter(p => (p.moq || 1) >= minMoq);
    const arr = [...r];
    arr.sort((a, b) => {
      if (sort === "price_asc") return (a.price || 0) - (b.price || 0);
      if (sort === "price_desc") return (b.price || 0) - (a.price || 0);
      if (sort === "name_desc") return (b.name || "").localeCompare(a.name || "");
      if (sort === "stock_desc") return (b.stock || 0) - (a.stock || 0);
      return (a.name || "").localeCompare(b.name || "");
    });
    return arr;
  }, [items, search, searchField, badgeSet, cats, brandSet, applyAttrFacets, attrSelKey, fulfillment, maxPrice, minMoq, sort]);

  // Inject the seller's own brand name onto every product so the card's brand
  // row renders (parity with buyer) and "Preview as buyer" mode shows it.
  const brandName = user?.company || user?.tenant_name || null;
  const displayRows = useMemo(() => filtered.map(p => ({ ...p, seller_name: p.seller_name || brandName })), [filtered, brandName]);

  // CRUD helpers
  const openCreate = () => { setEditing(null); setF(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setF({
      name: p.name || "", sku: p.sku || "", category: p.category || "", brand: p.brand || "",
      price: p.price ?? 0, moq: p.moq ?? 1, stock: p.stock ?? 0,
      image: p.image || "", description: p.description || "",
      map_price: p.map_price != null ? String(p.map_price) : "",
      retail_price: p.retail_price != null ? String(p.retail_price) : "",
      dropship_handling_fee: p.dropship_handling_fee != null ? String(p.dropship_handling_fee) : "",
      sub_category: p.sub_category || "", variant: p.variant || "", size: p.size || "",
      product_type: p.product_type || "", gender: p.gender || "", ean: p.ean || "",
      asin: p.asin || "", condition: p.condition || "", exp_date: p.exp_date || "", upc: p.upc || "",
      custom_fields: normalizeCustomFields(p.custom_fields),
      tiers: (p.tiers || []).map(t => ({ min_qty: String(t.min_qty), price: String(t.price) })),
    });
    setOpen(true);
  };
  const addTier = () => setF(f => ({ ...f, tiers: [...(f.tiers || []), { min_qty: "", price: "" }] }));
  const updTier = (i, k, v) => setF(f => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const rmTier = (i) => setF(f => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));
  const addCustom = () => setF(f => ({ ...f, custom_fields: [...(f.custom_fields || []), { label: "", value: "" }] }));
  const updCustom = (i, k, v) => setF(f => ({ ...f, custom_fields: f.custom_fields.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const rmCustom = (i) => setF(f => ({ ...f, custom_fields: f.custom_fields.filter((_, idx) => idx !== i) }));
  const save = async () => {
    try {
      const payload = {
        ...f,
        price: parseFloat(f.price) || 0,
        moq: parseInt(f.moq) || 1,
        stock: parseInt(f.stock) || 0,
        map_price: f.map_price === "" ? null : (parseFloat(f.map_price) || null),
        retail_price: f.retail_price === "" ? null : (parseFloat(f.retail_price) || null),
        dropship_handling_fee: f.dropship_handling_fee === "" ? null : (parseFloat(f.dropship_handling_fee) || null),
        custom_fields: (f.custom_fields || []).filter(c => c.label && c.label.trim())
          .map(c => ({ label: c.label.trim(), value: c.value || "" })),
        tiers: (f.tiers || []).filter(t => t.min_qty && t.price)
          .map(t => ({ min_qty: parseInt(t.min_qty), price: parseFloat(t.price) })),
      };
      if (editing) await api.patch(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const importCsv = async (file) => {
    if (!file) return;
    setCsvBusy(true); setCsvResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/products/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCsvResult(r.data); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setCsvBusy(false); }
  };

  return (
    <>
      <PageHeader title="Catalog" subtitle="Manage your wholesale products" action={
        <div className="flex flex-wrap items-center gap-2 xl:justify-end [&>*]:shrink-0">
          {catalogFeature.feature_enabled && <CatalogNeedsAttention mode="compact"/>}

          <Button variant="outline" onClick={() => setCustomizeOpen(true)} data-testid="customize-catalog-btn"
            className="rounded-full" title="Choose your catalog layout, which fields buyers see, and where it's visible">
            <Sliders className="w-4 h-4 mr-1.5"/>Customize
          </Button>

          {storefrontUrl && (
            <a href={`${storefrontUrl}/catalog`} target="_blank" rel="noopener noreferrer" data-testid="open-live-catalog"
              title="Open your public catalog webpage in a new tab — buyers browse & order from this store-style page"
              className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full text-[11.5px] transition-colors border bg-foreground text-background border-foreground hover:bg-foreground/90">
              <ExternalLink className="w-3.5 h-3.5"/>Live catalog
            </a>
          )}

          {/* Share / publish actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="share-menu-trigger"
                className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full text-[11.5px] transition-colors border bg-secondary/40 border-border text-foreground/70 hover:border-foreground/40">
                <Share2 className="w-3.5 h-3.5"/>Share<ChevronDown className="w-3 h-3 opacity-60"/>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setShareOpen(true)} data-testid="share-menu-link" disabled={!storefrontUrl}>
                <QrCode className="w-4 h-4 mr-2"/>Share link & QR code
              </DropdownMenuItem>
              {storefrontUrl && (
                <DropdownMenuItem onClick={() => window.open(storefrontUrl, "_blank", "noopener")} data-testid="share-menu-storefront">
                  <ExternalLink className="w-4 h-4 mr-2"/>View live storefront
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => nav("/seller/catalog/broadcast")} data-testid="share-menu-email">
                <Mail className="w-4 h-4 mr-2"/>Email catalog to buyers
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Infrequent tools */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="more-menu-trigger"
                className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full text-[11.5px] transition-colors border bg-secondary/40 border-border text-foreground/70 hover:border-foreground/40">
                <MoreHorizontal className="w-3.5 h-3.5"/>More<ChevronDown className="w-3 h-3 opacity-60"/>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem onClick={() => setCsvOpen(true)} data-testid="more-menu-csv">
                <Upload className="w-4 h-4 mr-2"/>Quick CSV import
              </DropdownMenuItem>
              {catalogFeature.feature_enabled && (
                <DropdownMenuItem onClick={() => nav("/seller/catalog/setup")} data-testid="more-menu-setup">
                  <Settings2 className="w-4 h-4 mr-2"/>Setup wizard
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => nav("/seller/settings#integrations")} data-testid="more-menu-api">
                <Plug className="w-4 h-4 mr-2"/>{apiEnabled ? "Buyer API · on" : "Buyer API / auto-sync"}
              </DropdownMenuItem>
              {catalogFeature.feature_enabled && (
                <>
                  <DropdownMenuSeparator/>
                  <DropdownMenuItem onClick={toggleCatalogApp} data-testid="more-menu-toggle">
                    <span className={`w-1.5 h-1.5 rounded-full mr-2 ${catalogFeature.enabled ? "bg-emerald-500" : "bg-foreground/30"}`}/>
                    Catalog visible to buyers · {catalogFeature.enabled ? "ON" : "OFF"}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {catalogFeature.feature_enabled && (
            <Button onClick={() => nav("/seller/catalog/import")} data-testid="ai-import-btn"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90">
              <Sparkles className="w-4 h-4 mr-1.5"/>AI import
            </Button>
          )}
          <Button className="rounded-full bg-foreground text-background hover:bg-foreground/90" onClick={openCreate} data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-1"/>Add product
          </Button>
        </div>
      }/>

      <div className="page-shell">
        {catalogFeature.feature_enabled && setupState && !setupState.completed_at && !setupState.skipped && (
          <div className="mb-6 rounded-2xl border border-foreground/15 bg-gradient-to-br from-foreground to-foreground/90 text-background p-5 flex items-start gap-4" data-testid="setup-banner">
            <div className="w-10 h-10 rounded-lg bg-accent text-foreground grid place-items-center shrink-0"><Sparkles className="w-5 h-5"/></div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] mono uppercase tracking-[0.22em] opacity-70">Welcome — let&apos;s set you up</div>
              <div className="font-medium text-base mt-1">Run the Catalog setup wizard</div>
              <p className="text-[12.5px] opacity-80 mt-1 leading-relaxed">
                4 quick steps · pick your industry, template, customize fields &amp; add custom data points, then start importing. Resumable any time.
              </p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button onClick={() => nav("/seller/catalog/setup")} data-testid="banner-start-setup"
                className="rounded-full bg-accent text-foreground hover:bg-accent/90 h-9 px-4 text-xs font-medium">Start setup →</Button>
              <button onClick={async () => {
                if (!window.confirm("Skip setup? You can always run it later from the Setup wizard button.")) return;
                try { await api.post("/seller/catalog-setup/skip"); setSetupState(s => ({ ...s, skipped: true })); toast.success("Setup skipped"); }
                catch (_e) { toast.error("Could not skip"); }
              }}
                className="text-[11px] opacity-65 hover:opacity-100 underline-offset-2 hover:underline">Skip for now</button>
            </div>
          </div>
        )}

        {(() => {
          const completedAt = setupState?.completed_at;
          if (!catalogFeature.feature_enabled || !completedAt) return null;
          const daysSince = (Date.now() - new Date(completedAt).getTime()) / 86400000;
          const dismissedFor = (() => { try { return localStorage.getItem("verqo_setup_done_dismissed"); } catch { return null; } })();
          // Show only for 7 days after setup/last re-run, and only until dismissed.
          if (daysSince > 7 || setupDoneDismissed || dismissedFor === String(completedAt)) return null;
          return (
            <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3 text-sm" data-testid="setup-done-banner">
              <Check className="w-4 h-4 text-emerald-700 shrink-0"/>
              <div className="flex-1 min-w-0">
                <span className="text-emerald-900 font-medium">Catalog setup complete</span>
                <span className="text-emerald-800/70 ml-2 text-[12px]">Industry: {setupState.industry_id} · Template: {setupState.template_id}</span>
              </div>
              <button onClick={() => nav("/seller/catalog/setup")} className="text-[11.5px] text-emerald-900/80 hover:text-emerald-900 underline-offset-2 hover:underline">Re-run wizard</button>
              <button onClick={() => { try { localStorage.setItem("verqo_setup_done_dismissed", String(completedAt)); } catch { /* ignore */ } setSetupDoneDismissed(true); }}
                data-testid="dismiss-setup-done" aria-label="Dismiss"
                className="w-6 h-6 grid place-items-center rounded-full text-emerald-900/50 hover:text-emerald-900 hover:bg-emerald-100 shrink-0">
                <X className="w-3.5 h-3.5"/>
              </button>
            </div>
          );
        })()}

        <CatalogLayoutShell
          testids={SELLER_TESTIDS}
          search={search} setSearch={setSearch}
          searchField={searchField} setSearchField={setSearchField}
          searchFields={SEARCH_FIELDS} searchPlaceholder="Search your products, SKU, UPC, category…"
          badgeSet={badgeSet} setBadgeSet={setBadgeSet}
          sort={sort} setSort={setSort} sortOptions={SORT_OPTS}
          categories={categories} cats={cats} setCats={setCats} totalCount={items.length}
          productBrands={brands} brandSet={brandSet} setBrandSet={setBrandSet}
          attrFilters={attrFilters}
          fulfillment={fulfillment} setFulfillment={setFulfillment}
          maxPrice={maxPrice} setMaxPrice={setMaxPrice} minMoq={minMoq} setMinMoq={setMinMoq}
          viewMode={viewMode} setViewMode={setViewMode} filteredCount={filtered.length}>
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-foreground/45 border border-dashed border-border rounded-2xl">
              {items.length === 0
                ? <><div className="display text-xl">Your catalog is empty</div><p className="text-xs mt-1">Click <strong>Add product</strong> or <strong>AI import</strong> to bring in your inventory.</p></>
                : <><div className="display text-xl">No matches</div><p className="text-xs mt-1">Adjust your filters to see more products.</p></>}
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="seller-grid">
              {displayRows.map(p => (
                <CatalogProductCard key={p.id} mode="seller" view="grid" p={p} vis={visibleFields} onEdit={() => openEdit(p)} onDelete={() => del(p.id)}/>
              ))}
            </div>
          ) : viewMode === "list" ? (
            <div className="space-y-2" data-testid="seller-list">
              {displayRows.map(p => (
                <CatalogProductCard key={p.id} mode="seller" view="list" p={p} onEdit={() => openEdit(p)} onDelete={() => del(p.id)}/>
              ))}
            </div>
          ) : (
            <CatalogTextTable mode="seller" rows={displayRows} onEdit={openEdit} onDelete={del}/>
          )}
        </CatalogLayoutShell>
      </div>

      <CustomizeCatalogPanel
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        viewMode={viewMode}
        setViewMode={setViewMode}
        catalogFeature={catalogFeature}
        onToggleCatalogApp={toggleCatalogApp}
        sampleProduct={items[0]}
        onVisibilityChange={setVisibleFields}
      />

      <ShareCatalogModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={storefrontUrl ? `${storefrontUrl}/catalog` : null}
        sellerName={user?.company || user?.tenant_name}
      />

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit · ${editing.sku}` : "New product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="prod-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>SKU</Label>
                <Input data-testid="prod-sku" value={f.sku} disabled={!!editing} onChange={e => setF({ ...f, sku: e.target.value })}/>
                {editing && <div className="text-[10px] text-foreground/50 mt-0.5">SKU is immutable</div>}
              </div>
              <div><Label>Category</Label><Input value={f.category} onChange={e => setF({ ...f, category: e.target.value })}/></div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div><Label>Brand</Label><Input data-testid="prod-brand" value={f.brand} onChange={e => setF({ ...f, brand: e.target.value })} placeholder="e.g. Nike, Acme, your house brand"/></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Base price</Label><Input type="number" step="0.01" data-testid="prod-price" value={f.price} onChange={e => setF({ ...f, price: e.target.value })}/></div>
              <div><Label>MOQ</Label><Input type="number" value={f.moq} onChange={e => setF({ ...f, moq: e.target.value })}/></div>
              <div><Label>Stock</Label><Input type="number" value={f.stock} onChange={e => setF({ ...f, stock: e.target.value })}/></div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5"/>MAP price (Minimum Advertised Price)</Label>
              <Input type="number" step="0.01" data-testid="prod-map" value={f.map_price} placeholder="leave blank if no MAP enforced" onChange={e => setF({ ...f, map_price: e.target.value })}/>
            </div>
            <div><Label>Retail / MSRP price</Label><Input type="number" step="0.01" data-testid="prod-retail" value={f.retail_price} placeholder="suggested retail" onChange={e => setF({ ...f, retail_price: e.target.value })}/></div>
            <div>
              <Label className="flex items-center gap-1.5">Dropship handling fee (per unit)</Label>
              <Input
                type="number" step="0.01" data-testid="prod-dropship-handling"
                value={f.dropship_handling_fee}
                placeholder="0.00 — adds on top of wholesale on dropship orders"
                onChange={e => setF({ ...f, dropship_handling_fee: e.target.value })}
              />
              <div className="text-[10.5px] text-foreground/55 mt-1">
                Per-unit pick/pack fee buyers see in their dropship catalog. Leave blank if shipping is already included in your wholesale price.
              </div>
            </div>

            {/* Extended catalog attributes */}
            <div className="rounded-lg border border-border p-3 bg-foreground/[0.02] space-y-3">
              <div className="text-sm font-medium">Product details</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[11px]">Sub-category</Label><Input data-testid="prod-subcategory" value={f.sub_category} onChange={e => setF({ ...f, sub_category: e.target.value })}/></div>
                <div><Label className="text-[11px]">Product type</Label><Input data-testid="prod-type" value={f.product_type} onChange={e => setF({ ...f, product_type: e.target.value })}/></div>
                <div><Label className="text-[11px]">Variant</Label><Input data-testid="prod-variant" value={f.variant} onChange={e => setF({ ...f, variant: e.target.value })}/></div>
                <div><Label className="text-[11px]">Size</Label><Input data-testid="prod-size" value={f.size} onChange={e => setF({ ...f, size: e.target.value })} placeholder="e.g. 100ml, L"/></div>
                <div><Label className="text-[11px]">Gender</Label><Input data-testid="prod-gender" value={f.gender} onChange={e => setF({ ...f, gender: e.target.value })} placeholder="Men / Women / Unisex"/></div>
                <div>
                  <Label className="text-[11px]">Condition</Label>
                  <select data-testid="prod-condition" value={f.condition} onChange={e => setF({ ...f, condition: e.target.value })}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">—</option>
                    <option value="Clean">Clean</option>
                    <option value="Decoded">Decoded</option>
                    <option value="Tester">Tester</option>
                  </select>
                </div>
                <div><Label className="text-[11px]">Expiration date</Label><Input type="date" data-testid="prod-exp" value={f.exp_date} onChange={e => setF({ ...f, exp_date: e.target.value })}/></div>
              </div>
            </div>

            {/* Identifiers */}
            <div className="rounded-lg border border-border p-3 bg-foreground/[0.02]">
              <div className="text-sm font-medium mb-2">Identifiers</div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-[11px]">UPC</Label><Input data-testid="prod-upc" value={f.upc} onChange={e => setF({ ...f, upc: e.target.value })}/></div>
                <div><Label className="text-[11px]">EAN</Label><Input data-testid="prod-ean" value={f.ean} onChange={e => setF({ ...f, ean: e.target.value })}/></div>
                <div><Label className="text-[11px]">Amazon ASIN</Label><Input data-testid="prod-asin" value={f.asin} onChange={e => setF({ ...f, asin: e.target.value })}/></div>
              </div>
            </div>

            {/* Custom fields */}
            <div className="rounded-lg border border-border p-3 bg-foreground/[0.02]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">Custom fields</div>
                <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={addCustom} data-testid="custom-add"><Plus className="w-3 h-3 mr-1"/>Add field</Button>
              </div>
              {(f.custom_fields || []).length === 0 && <div className="text-xs text-foreground/55 py-1">Add your own attributes (e.g. Concentration → EDP). Shown read-only to buyers.</div>}
              {(f.custom_fields || []).map((c, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2" data-testid={`custom-row-${idx}`}>
                  <Input placeholder="Label" value={c.label} onChange={e => updCustom(idx, "label", e.target.value)} data-testid={`custom-label-${idx}`}/>
                  <Input placeholder="Value" value={c.value} onChange={e => updCustom(idx, "value", e.target.value)} data-testid={`custom-value-${idx}`}/>
                  <button onClick={() => rmCustom(idx)} className="text-foreground/40 hover:text-destructive self-center" data-testid={`custom-rm-${idx}`}><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-border p-3 bg-foreground/[0.02]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium flex items-center gap-1.5"><Layers className="w-3.5 h-3.5"/>Volume tiers</div>
                <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={addTier} data-testid="tier-add"><Plus className="w-3 h-3 mr-1"/>Add tier</Button>
              </div>
              {(f.tiers || []).length === 0 && <div className="text-xs text-foreground/55 py-2">No tiers — base price applies to all quantities.</div>}
              {(f.tiers || []).map((t, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2" data-testid={`tier-row-${idx}`}>
                  <div><Label className="text-[10px]">Min qty</Label><Input type="number" min="1" value={t.min_qty} onChange={e => updTier(idx, "min_qty", e.target.value)}/></div>
                  <div><Label className="text-[10px]">Per-unit $</Label><Input type="number" step="0.01" value={t.price} onChange={e => updTier(idx, "price", e.target.value)}/></div>
                  <button onClick={() => rmTier(idx)} className="text-foreground/40 hover:text-destructive self-end pb-2" data-testid={`tier-rm-${idx}`}><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              ))}
            </div>
            <div><Label>Image URL</Label><Input value={f.image} onChange={e => setF({ ...f, image: e.target.value })}/></div>
            <Button onClick={save} data-testid="prod-submit" className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90">
              {editing ? "Save changes" : "Add product"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV import dialog */}
      <Dialog open={csvOpen} onOpenChange={(o) => { setCsvOpen(o); if (!o) setCsvResult(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Import products from CSV</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-foreground/[0.04] p-3 text-xs leading-relaxed">
              <div className="font-medium mb-1">Required columns: <code>name, sku, price</code></div>
              <div className="text-foreground/70">Optional: <code>category, moq, stock, image, description, map_price</code></div>
            </div>
            <input ref={csvRef} type="file" accept=".csv,text/csv" onChange={(e) => importCsv(e.target.files?.[0])}
              disabled={csvBusy} data-testid="csv-file-input" className="block w-full text-sm"/>
            {csvBusy && <div className="text-xs text-foreground/60">Importing…</div>}
            {csvResult && (
              <div className="rounded-lg border border-border p-3 text-xs space-y-1">
                <div className="font-medium">Done.</div>
                <div>Inserted: <strong>{csvResult.inserted}</strong></div>
                <div>Updated: <strong>{csvResult.updated}</strong></div>
                <div>Failed: <strong>{csvResult.failed_count}</strong></div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pre-publish confirm */}
      <Dialog open={!!prePublish} onOpenChange={(v) => !v && setPrePublish(null)}>
        <DialogContent className="max-w-md" data-testid="prepublish-confirm">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight inline-flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-600"/>Go live now?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/70 -mt-1">
            Your catalog has {prePublish?.total} product{prePublish?.total !== 1 ? "s" : ""} but {prePublish?.gaps?.length} type{(prePublish?.gaps?.length || 0) !== 1 ? "s" : ""} of gaps. Buyers will see your catalog with these gaps unless you fix them first.
          </p>
          <ul className="space-y-1 mt-1 text-[12px]">
            {(prePublish?.gaps || []).map(g => (
              <li key={g.field} className="rounded-md bg-amber-50 border border-amber-200 px-3 py-1.5 text-amber-900"><span className="mono mr-2">{g.count}</span>{g.label}</li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setPrePublish(null)} className="rounded-full" data-testid="prepublish-cancel">Fix first</Button>
            <Button onClick={() => { setPrePublish(null); _flipCatalogOn(); }} data-testid="prepublish-go" className="rounded-full bg-foreground text-background hover:bg-foreground/90">Go live anyway</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
